import { useSyncExternalStore } from 'react';

/*
 * r16p (design 7.1 / 7.6): reply drafts in MEMORY only - a module Map, like lib/assistantDraft.js. A draft is third-party-context
 * text the customer is writing to a real person: never localStorage, sessionStorage, a URL or a console line. It survives switching
 * conversations and coming back (each conversation keeps its own draft), and it is gone on reload or sign-out.
 *
 * Keyed by (account, conversation id): a draft typed in conversation A can never be shown, or sent, in conversation B.
 */
const drafts = new Map();
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const keyOf = (owner, threadId) => `${owner || 'signed-out'}|${threadId || '-'}`;

export function getChatDraft(owner, threadId) {
  return drafts.get(keyOf(owner, threadId)) || '';
}

export function setChatDraft(owner, threadId, value) {
  const k = keyOf(owner, threadId);
  const next = typeof value === 'function' ? value(drafts.get(k) || '') : value;
  if (next) drafts.set(k, String(next)); else drafts.delete(k);
  listeners.forEach((fn) => fn());
}

/** [draft, setDraft] for one conversation; switching `threadId` switches the draft. */
export function useChatDraft(owner, threadId) {
  const text = useSyncExternalStore(subscribe, () => getChatDraft(owner, threadId));
  return [text, (v) => setChatDraft(owner, threadId, v)];
}

/** Every draft of every account (components/ChatsCacheGuard.js: on sign-out and on an account change, review T1). */
export function clearChatDrafts() {
  drafts.clear();
  listeners.forEach((fn) => fn());
}

/** Tests only - module state would otherwise leak between cases. */
export function __resetChatDrafts() {
  drafts.clear();
}
